import type { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { Pencil, TestTube2, Trash2 } from 'lucide-react'
import type {
    FeatureFlag,
    FeatureFlagCategory,
    FeatureFlagGroup,
    FeatureFlagStatus,
    FeatureFlagTargetType,
} from '@/types/feature-flag'

const statusStyles: Record<FeatureFlagStatus, string> = {
    ACTIVE: 'bg-green-100 text-green-700 hover:bg-green-100',
    INACTIVE: 'bg-gray-100 text-gray-700 hover:bg-gray-100',
    ARCHIVED: 'bg-red-100 text-red-700 hover:bg-red-100',
}

const categoryStyles: Record<FeatureFlagCategory, string> = {
    CORE: 'bg-blue-50 text-blue-700 hover:bg-blue-50',
    PREMIUM: 'bg-purple-50 text-purple-700 hover:bg-purple-50',
    BETA: 'bg-orange-50 text-orange-700 hover:bg-orange-50',
    EXPERIMENTAL: 'bg-pink-50 text-pink-700 hover:bg-pink-50',
}

/** One-line summary of who a flag reaches, shown in the table. */
const describeAudience = (flag: FeatureFlag) => {
    const labels: Record<FeatureFlagTargetType, string> = {
        ALL: 'Everyone',
        SPECIFIC: `${flag.targetUserIds.length} user${flag.targetUserIds.length === 1 ? '' : 's'}`,
        GROUP: flag.targetGroups?.length
            ? flag.targetGroups.map(group => group.name).join(', ')
            : 'No groups',
        RULE: `${flag.targetRules?.length ?? 0} rule${(flag.targetRules?.length ?? 0) === 1 ? '' : 's'}`,
        PERCENTAGE: `${flag.rolloutPercentage}% of users`,
    }

    const limits: string[] = []
    if (flag.platforms?.length) limits.push(flag.platforms.join('/'))
    if (flag.minAppVersion) limits.push(`≥ ${flag.minAppVersion}`)
    if (flag.maxAppVersion) limits.push(`≤ ${flag.maxAppVersion}`)

    return limits.length
        ? `${labels[flag.targetType]} · ${limits.join(' ')}`
        : labels[flag.targetType]
}

interface FeatureFlagColumnOptions {
    onToggle: (flag: FeatureFlag) => void
    onEdit: (flag: FeatureFlag) => void
    onPreview: (flag: FeatureFlag) => void
    onDelete: (flag: FeatureFlag) => void
    togglingId?: string | null
}

export const buildFeatureFlagColumns = ({
    onToggle,
    onEdit,
    onPreview,
    onDelete,
    togglingId,
}: FeatureFlagColumnOptions): ColumnDef<FeatureFlag>[] => [
    {
        accessorKey: 'name',
        header: 'Flag',
        cell: ({ row }) => (
            <div className="min-w-48">
                <p className="text-sm font-medium text-[#0F172A]">
                    {row.original.name}
                </p>
                <p className="font-mono text-xs text-muted-foreground">
                    {row.original.key}
                </p>
            </div>
        ),
    },
    {
        accessorKey: 'category',
        header: 'Category',
        cell: ({ row }) => (
            <Badge
                variant="secondary"
                className={categoryStyles[row.original.category]}
            >
                {row.original.category}
            </Badge>
        ),
    },
    {
        id: 'audience',
        header: 'Audience',
        cell: ({ row }) => (
            <span className="text-sm text-[#0F172A]">
                {describeAudience(row.original)}
            </span>
        ),
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
            <Badge
                variant="secondary"
                className={statusStyles[row.original.status]}
            >
                {row.original.status}
            </Badge>
        ),
    },
    {
        id: 'toggle',
        header: 'On',
        cell: ({ row }) => (
            <Switch
                checked={row.original.status === 'ACTIVE'}
                disabled={
                    row.original.status === 'ARCHIVED' ||
                    togglingId === row.original.id
                }
                onCheckedChange={() => onToggle(row.original)}
            />
        ),
    },
    {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
            <div className="flex justify-end gap-1">
                <Button
                    variant="ghost"
                    size="icon"
                    title="Test"
                    onClick={() => onPreview(row.original)}
                >
                    <TestTube2 className="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Edit"
                    onClick={() => onEdit(row.original)}
                >
                    <Pencil className="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    className="text-muted-foreground hover:text-red-600"
                    onClick={() => onDelete(row.original)}
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        ),
    },
]

interface GroupColumnOptions {
    onEdit: (group: FeatureFlagGroup) => void
    onPreview: (group: FeatureFlagGroup) => void
    onDelete: (group: FeatureFlagGroup) => void
}

export const buildFeatureFlagGroupColumns = ({
    onEdit,
    onPreview,
    onDelete,
}: GroupColumnOptions): ColumnDef<FeatureFlagGroup>[] => [
    {
        accessorKey: 'name',
        header: 'Group',
        cell: ({ row }) => (
            <div className="min-w-48">
                <p className="text-sm font-medium text-[#0F172A]">
                    {row.original.name}
                </p>
                <p className="font-mono text-xs text-muted-foreground">
                    {row.original.key}
                </p>
            </div>
        ),
    },
    {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => (
            <Badge
                variant="secondary"
                className={
                    row.original.type === 'STATIC'
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-100'
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-50'
                }
            >
                {row.original.type}
            </Badge>
        ),
    },
    {
        id: 'size',
        header: 'Users',
        cell: ({ row }) => (
            <span className="text-sm text-[#0F172A]">
                {row.original.type === 'STATIC'
                    ? row.original.userIds.length
                    : (row.original.cachedCount ?? 0)}
                {row.original.type === 'DYNAMIC' && (
                    <span className="ml-1 text-xs text-muted-foreground">
                        {row.original.lastEvaluatedAt
                            ? '(last count)'
                            : '(not counted yet)'}
                    </span>
                )}
            </span>
        ),
    },
    {
        id: 'flags',
        header: 'Used by',
        cell: ({ row }) => (
            <span className="text-sm text-[#0F172A]">
                {row.original._count?.flags ?? row.original.flags?.length ?? 0}{' '}
                flag(s)
            </span>
        ),
    },
    {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
            <div className="flex justify-end gap-1">
                <Button
                    variant="ghost"
                    size="icon"
                    title="Count users"
                    onClick={() => onPreview(row.original)}
                >
                    <TestTube2 className="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Edit"
                    onClick={() => onEdit(row.original)}
                >
                    <Pencil className="h-4 w-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="icon"
                    title="Delete"
                    className="text-muted-foreground hover:text-red-600"
                    onClick={() => onDelete(row.original)}
                >
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        ),
    },
]
